import { ComparativeDisplayType, ZeroDisplayType as LegacyZeroDisplayType } from "../types/pricing";

import { ZeroDisplayType } from "@vp/ep-pricing";
import { Locale } from "./locales";

export enum PriceType {
  TotalListPrice = "totalListPrice",
  TotalDiscountedPrice = "totalDiscountedPrice",
  UnitListPrice = "unitListPrice",
  UnitDiscountedPrice = "unitDiscountedPrice",
  LowestListPrice = "lowestListPrice",
  HighestListPrice = "highestListPrice",
  LowestDiscountPrice = "lowestDiscountPrice",
  HighestDiscountPrice = "highestDiscountPrice",
  PriceDifference = "priceDifference",
  PriceDifferenceDiscounted = "priceDifferenceDiscounted",
  SetupFeeListPrice = "setupFeeListPrice",
  MinimumQuantity = "minimumQuantity",
  SetupFeeDiscountedPrice = "setupFeeDiscountedPrice",
  UnitListPriceForMoq = "unitListPriceForMoq",
  BaseListPrice = "baseListPrice",
  BaseDiscountedPrice = "baseDiscountedPrice",
  UnitPriceDifference = "unitPriceDifference",
  UnitPriceDifferenceDiscounted = "unitPriceDifferenceDiscounted",
}

export enum PricePresentationType {
  QtyStartingAtPrice = "qtyStartingAtPrice",
  QtyFromPrice = "qtyFromPrice",
  AsLowAsPricePerPiece = "asLowAsPricePerPiece",
  PricePerUnit = "pricePerUnit",
  PlusOrMinus = "plusOrMinus",
  RawPrice = "rawPrice",
  RangePrice = "rangePrice",
  UnitRangePrice = "unitRangePrice",
  UnitRangePriceForTile = "unitRangePriceForTile",
  PriceForQtyUnits = "priceForQtyUnits",
  PriceWithQtyAndUnit = "priceWithQtyAndUnit",
  RawAndPricePerUnit = "rawAndPricePerUnit",
  PriceInfo = "priceInfo",
  UnitPriceInfo = "unitPriceInfo",
  DetailedPriceInfo = "detailedPriceInfo",
  FromPrice = "fromPrice",
  UnitFromPrice = "unitFromPrice",
  DifferentialPrice = "differentialPrice",
  DifferentialPriceForQuantity = "differentialPriceForQuantity",
  SetupFee = "setupFee",
  QtyForPrice = "qtyForPrice",
  TotalPrice = "totalPrice",
  DifferentialUnitRangePrice = "differentialUnitRangePrice",
  UnitDifferentialPrice = "unitDifferentialPrice",
  PriceWithSavings = "priceWithSavings",
}

export interface PriceProperties {
  minimumFractionDigits: number;
  locale: Locale | string;
  tenant?: string;
  currency: string;
  vatInc: boolean;
  quantity?: number;
  moq?: number;
  hideVatMessage?: boolean;
  hideShippingMessageInVatMessage?: boolean;
  isPriceNegative?: boolean;
  zeroDisplayType?: ZeroDisplayType | LegacyZeroDisplayType;
  comparativeDisplayType?: ComparativeDisplayType;
  showComparativeDisplay?: boolean;
  isSetupFeeIncluded?: boolean;
  hideSetupFee?: boolean;
  hidePerUnitPrice?: boolean;
  hideVolumeDiscount?: boolean;
  hideMinimumOrderableQuantity?: boolean;
  alwaysShowMOQInfo?: boolean;
}

export interface Price {
  taxed: number;
  untaxed: number;
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const PricePresentationToPriceTypesMap = {
  qtyStartingAtPrice: [PriceType.TotalListPrice, PriceType.TotalDiscountedPrice],
  qtyFromPrice: [PriceType.TotalListPrice, PriceType.TotalDiscountedPrice],
  asLowAsPricePerPiece: [PriceType.UnitListPrice, PriceType.UnitDiscountedPrice],
  pricePerUnit: [PriceType.UnitListPrice, PriceType.UnitDiscountedPrice],
  plusOrMinus: [PriceType.PriceDifference, PriceType.PriceDifferenceDiscounted],
  rawPrice: [PriceType.TotalListPrice, PriceType.TotalDiscountedPrice],
  rangePrice: [
    PriceType.HighestListPrice,
    PriceType.HighestDiscountPrice,
    PriceType.LowestListPrice,
    PriceType.LowestDiscountPrice,
  ] as const,
  unitRangePrice: [
    PriceType.HighestListPrice,
    PriceType.HighestDiscountPrice,
    PriceType.LowestListPrice,
    PriceType.LowestDiscountPrice,
  ] as const,
  unitRangePriceForTile: [
    PriceType.HighestListPrice,
    PriceType.HighestDiscountPrice,
    PriceType.LowestListPrice,
    PriceType.LowestDiscountPrice,
  ] as const,
  priceForQtyUnits: [PriceType.TotalListPrice, PriceType.TotalDiscountedPrice],
  priceWithQtyAndUnit: [
    PriceType.UnitListPrice,
    PriceType.UnitDiscountedPrice,
    PriceType.TotalListPrice,
    PriceType.TotalDiscountedPrice,
  ] as const,
  rawAndPricePerUnit: [
    PriceType.TotalListPrice,
    PriceType.TotalDiscountedPrice,
    PriceType.UnitListPrice,
    PriceType.UnitDiscountedPrice,
  ] as const,
  totalPrice: [
    PriceType.TotalListPrice,
    PriceType.TotalDiscountedPrice,
  ] as const,
  priceInfo: [
    PriceType.TotalListPrice,
    PriceType.TotalDiscountedPrice,
  ] as const,
  unitPriceInfo: [
    PriceType.TotalListPrice,
    PriceType.TotalDiscountedPrice,
  ] as const,
  detailedPriceInfo: [
    PriceType.TotalListPrice,
    PriceType.TotalDiscountedPrice,
  ] as const,
  fromPrice: [
    PriceType.TotalListPrice,
    PriceType.TotalDiscountedPrice,
  ] as const,
  unitFromPrice: [
    PriceType.TotalListPrice,
    PriceType.TotalDiscountedPrice,
  ] as const,
  differentialPrice: [PriceType.PriceDifference, PriceType.PriceDifferenceDiscounted],
  differentialPriceForQuantity: [PriceType.PriceDifference, PriceType.PriceDifferenceDiscounted],
  qtyForPrice: [
    PriceType.TotalListPrice,
    PriceType.TotalDiscountedPrice,
  ] as const,
  setupFee: [PriceType.SetupFeeDiscountedPrice, PriceType.SetupFeeListPrice],
  differentialUnitRangePrice: [
    PriceType.HighestDiscountPrice,
    PriceType.LowestDiscountPrice,
    PriceType.HighestListPrice,
    PriceType.LowestListPrice,
  ] as const,
  unitDifferentialPrice: [PriceType.PriceDifference, PriceType.PriceDifferenceDiscounted],
} as const;

export type PricesTypesBasedOnPricePresentation<
  T extends keyof typeof PricePresentationToPriceTypesMap,
> = (typeof PricePresentationToPriceTypesMap)[T][number];
